/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";

// Material Dashboard 2 React base styles
import colors from "app/theme/base/colors";
import breakpoints from "app/theme/base/breakpoints";
import typography from "app/theme/base/typography";
import boxShadows from "app/theme/base/boxShadows";
import borders from "app/theme/base/borders";
import globals from "app/theme/base/globals";

// Material Dashboard 2 React helper functions
import boxShadow from "app/theme/functions/boxShadow";
import hexToRgb from "app/theme/functions/hexToRgb";
import linearGradient from "app/theme/functions/linearGradient";
import pxToRem from "app/theme/functions/pxToRem";
import rgba from "app/theme/functions/rgba";

// Material Dashboard 2 React components base styles for @mui material components
import sidenav from "app/theme/components/sidenav";
import list from "app/theme/components/list";
import listItem from "app/theme/components/list/listItem";
import listItemText from "app/theme/components/list/listItemText";
import card from "app/theme/components/card";
import cardMedia from "app/theme/components/card/cardMedia";
import cardContent from "app/theme/components/card/cardContent";
import button from "app/theme/components/button";
import iconButton from "app/theme/components/iconButton";
import input from "app/theme/components/form/input";
import inputLabel from "app/theme/components/form/inputLabel";
import inputOutlined from "app/theme/components/form/inputOutlined";
import textField from "app/theme/components/form/textField";
import menu from "app/theme/components/menu";
import menuItem from "app/theme/components/menu/menuItem";
import switchButton from "app/theme/components/form/switchButton";
import divider from "app/theme/components/divider";
import tableContainer from "app/theme/components/table/tableContainer";
import tableHead from "app/theme/components/table/tableHead";
import tableCell from "app/theme/components/table/tableCell";
import linearProgress from "app/theme/components/linearProgress";
import breadcrumbs from "app/theme/components/breadcrumbs";
import slider from "app/theme/components/slider";
import avatar from "app/theme/components/avatar";
import tooltip from "app/theme/components/tooltip";
import appBar from "app/theme/components/appBar";
import tabs from "app/theme/components/tabs";
import tab from "app/theme/components/tabs/tab";
import stepper from "app/theme/components/stepper";
import step from "app/theme/components/stepper/step";
import stepConnector from "app/theme/components/stepper/stepConnector";
import stepLabel from "app/theme/components/stepper/stepLabel";
import stepIcon from "app/theme/components/stepper/stepIcon";
import select from "app/theme/components/form/select";
import formControlLabel from "app/theme/components/form/formControlLabel";
import formLabel from "app/theme/components/form/formLabel";
import checkbox from "app/theme/components/form/checkbox";
import radio from "app/theme/components/form/radio";
import autocomplete from "app/theme/components/form/autocomplete";
import container from "app/theme/components/container";
import popover from "app/theme/components/popover";
import buttonBase from "app/theme/components/buttonBase";
import icon from "app/theme/components/icon";
import svgIcon from "app/theme/components/svgIcon";
import link from "app/theme/components/link";
import dialog from "app/theme/components/dialog";
import dialogTitle from "app/theme/components/dialog/dialogTitle";
import dialogContent from "app/theme/components/dialog/dialogContent";
import dialogContentText from "app/theme/components/dialog/dialogContentText";
import dialogActions from "app/theme/components/dialog/dialogActions";

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
});
